import React, { useMemo } from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';
import { History } from 'history';
import { PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { checkUserHasCapabilities } from '@townsquare/capability-provider/checkUserHasCapabilities';
import { type CollaborationContext } from '@townsquare/collab-context';
import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import type { CapabilityRouterFragment$key } from '@townsquare/router/types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';
import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { BootstrapShell, type BootstrapShellData } from './BootstrapShell';
import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen } from './ErrorBoundary/scenarios';
import { BootstrapProjectsQuery } from './__generated__/BootstrapProjectsQuery.graphql';
import { deriveContextCloudId } from './deriveBootstrap';

export type BootstrapResult = {
	orgId?: string;
	cloudIds: string[];
	workspaceIds: string[];
	workspaceUuids: string[];
	workspace?: WorkspaceDetails;
	user: User;
	relayKey: CapabilityRouterFragment$key | undefined;
	collabContext?: CollaborationContext | null;
};

type BootstrapProps = {
	children: (data: BootstrapResult) => JSX.Element;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	queryRef: PreloadedQuery<BootstrapProjectsQuery>;
};

/**
 * GraphQL branch of the projects bootstrap — does the Relay-specific work
 * (unmask preloaded query, call fragment-ref versions of `initWorkspaceFromHome`
 * and `checkUserHasCapabilities`), then hands the flattened plain-data shape
 * to `BootstrapShell` for everything else.
 *
 * WHAT STAYS HERE: anything that needs the Relay data source (`usePreloadedQuery`,
 * fragment-ref-taking helpers). WHAT GOES TO THE SHELL: all branch-agnostic
 * side effects (org context, collab context, statsig init, monitoring, route
 * preload, app-state transition).
 *
 * Deleted when Volt is at 100% on `/project/:id`. Sibling: `BootstrapFromVolt.tsx`.
 */
function BootstrapResolution(props: BootstrapProps): JSX.Element {
	const data = usePreloadedQuery<BootstrapProjectsQuery>(
		// ⚠️ DRIFT: keep this selection aligned with the server-owned source of
		// truth at `controllers/bootstrap/projectsBootstrapQuery.ts`. Edit in
		// lockstep until this client-side path is deleted at Volt-100%.
		graphql`
			query BootstrapProjectsQuery($context: OrganizationContextInput) {
				bootstrap(organizationContext: $context) {
					orgId
					cloudIds
					collabContext {
						cloudIds
						isMultiSite
						hasMultipleUnits
					}
					userIsOrgAdmin
					workspaces {
						id
						uuid
						cloudId
						type
						...storeHomeWorkspace
						...CapabilityRouterFragment
						...utils_checkUserHasCapabilities
					}
				}
			}
		`,
		props.queryRef,
	);

	const orgId = data.bootstrap?.orgId ?? undefined;
	// All derivations memoised for parity with the Volt branch — shellData
	// carries these into setOrgContext and downstream consumers; stable refs
	// avoid spurious re-runs. Master had `orgCloudIds: ... ?? null`; preserve.
	const orgCloudIds = useMemo(
		() => data.bootstrap?.cloudIds?.filter(filterFalsyStrings) ?? null,
		[data.bootstrap?.cloudIds],
	);
	const cloudIds = useMemo(() => orgCloudIds ?? [], [orgCloudIds]);
	const workspaces = useMemo(
		() => data.bootstrap?.workspaces?.filter(filterNull) ?? [],
		[data.bootstrap?.workspaces],
	);
	const workspaceIds = useMemo(
		() => workspaces.map((workspace) => workspace.id).filter(filterFalsyStrings),
		[workspaces],
	);
	const workspaceUuids = useMemo(
		() => workspaces.map((workspace) => workspace.uuid).filter(filterFalsyStrings),
		[workspaces],
	);

	/**
	 * Titan is site-scoped
	 * The ordering here is as follows:
	 * 1. If the query has a context (aka we are hitting a deep link and we're passing a cloudId to the query), use that for searching for the workspace
	 * 2. If the query has no context, use the first cloudId in the list as that is ordered by the backend to be the most relevant (preference or otherwise based on the user)
	 * 3. If there are no cloudIds, default to null (aka no workspace exists for the current request)
	 */
	const contextCloudId = useMemo(
		() => deriveContextCloudId(props.queryRef.variables.context?.cloudId ?? undefined, cloudIds),
		[props.queryRef.variables.context?.cloudId, cloudIds],
	);
	const workspace = useMemo(
		() =>
			workspaces
				// Exclude PRODUCT workspaces, treat them as no workspace
				.filter((w) => w.type === 'GLOBAL_EXPERIENCE')
				.find((w) => w.cloudId === contextCloudId),
		[workspaces, contextCloudId],
	);

	const workspaceActions = useWorkspaceActions();
	let workspaceDetails: WorkspaceDetails | undefined;
	if (workspace && orgId) {
		workspaceDetails = workspaceActions.initWorkspaceFromHome(workspace, {
			numWorkspaces: workspaceIds.length,
			userIsOrgAdmin: data.bootstrap?.userIsOrgAdmin ?? false,
			organisationId: orgId,
		});
	}

	const userHasProjectAccess = workspace
		? checkUserHasCapabilities(workspace, ['projects_app:access'], true)
		: false;

	const collabContextSource = useMemo(
		() =>
			data.bootstrap?.collabContext
				? {
						cloudIds: data.bootstrap.collabContext.cloudIds.filter(filterFalsyStrings),
						isMultiSite: data.bootstrap.collabContext.isMultiSite ?? false,
						hasMultipleUnits: data.bootstrap.collabContext.hasMultipleUnits ?? false,
					}
				: null,
		[data.bootstrap?.collabContext],
	);

	const shellData: BootstrapShellData = {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource,
		activeWorkspace: workspace ? { cloudId: workspace.cloudId, uuid: workspace.uuid } : undefined,
		userHasProjectAccess,
	};

	return (
		<BootstrapShell
			bootstrap={shellData}
			workspaceDetails={workspaceDetails}
			matchedRoute={props.matchedRoute}
			history={props.history}
		>
			{({ collabContext, currentUser }) =>
				props.children({
					orgId,
					cloudIds,
					workspaceIds,
					workspaceUuids,
					workspace: workspaceDetails,
					user: currentUser,
					relayKey: workspace,
					collabContext,
				})
			}
		</BootstrapShell>
	);
}

export function Bootstrap(props: BootstrapProps): React.JSX.Element {
	// Must run before <Suspense>: BootstrapErrorBoundary reads it when the populator throws.
	userAccessRedirectContextHolder.initUserAccessRedirect(routes);

	if (props.matchedRoute?.route.name === '404-page') {
		return <NotFoundScreen />;
	}

	return (
		<BootstrapErrorBoundary>
			<Suspense interactionName="spa-loader" fallback={<FullScreenSpaLoader />}>
				<BootstrapResolution {...props} />
			</Suspense>
		</BootstrapErrorBoundary>
	);
}
